import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, ElementRef, forwardRef, Input, Renderer2 } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import type { AppSelectOption } from '@shared/form-fields/options/select-option';
import { BaseSelectFormFieldComponent } from '@shared/form-fields/select/base-select-form-field.component';
import { ApiParams } from '@shared/http/api-params';
import { toApiObservable } from '@shared/models/api-state/operators';
import type { Team } from '@shared/modules/teams/team';
import { isNonNull } from '@shared/rx/is-non-null';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs/internal/Subject';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { isPresent } from '../../../utils/helpers';
import type { Carer } from '../carer';
import { CarerFetcher } from '../carer.fetcher';
import { CarerPresenter } from '../carer.presenter';

@Component({
    selector: 'app-carer-select',
    styleUrls: ['./carer-select.component.scss'],
    templateUrl: '../../../form-fields/select/select-form-field.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CarerSelectComponent), multi: true }],
})
@UntilDestroy()
export class CarerSelectComponent extends BaseSelectFormFieldComponent<Carer> implements OnInit, OnChanges {
    private _active: boolean | DateTime;
    private _activeBetween: [string, string];

    @Input() params: ApiParams;
    @Input() fetchFromApi = true;

    @Input() teams: Team[];

    @Input() set active(value: boolean | DateTime) {
        this._active = value;
        this._fetch();
    }

    @Input() set activeBetween(value: [string, string]) {
        this._activeBetween = value;

        this._fetch();
    }

    private fetchParams$ = new Subject<ApiParams>();
    private fetch$ = this.fetchParams$.pipe(
        switchMap(params => this.fetcher.all(params).pipe(
            toApiObservable()
        )),
        map(response => response.transform(carers => this._buildOptions(carers)))
    );

    constructor(element: ElementRef,
                renderer: Renderer2,
                private fetcher: CarerFetcher) {
        super(element, renderer);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.fetch$.pipe(
            untilDestroyed(this),
            tap(response => this.fetching = response.fetching),
            map(response => response.data),
            filter(isNonNull)
        ).subscribe(options => this.apiOptions = options);

        this._fetch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this._fetch();
        }

        if (changes['teams']) {
            this._fetch();
        }
    }

    private _fetch() {
        if (!this.fetchFromApi) {
            return;
        }

        const params = {
            order_by: 'last_name',
            direction: 'asc',
            ...this.params
        };

        if (this.teams) {
            params['team'] = this.teams.map(t => t.id);
        }

        if (isPresent(this._active)) {
            if (this._active instanceof DateTime) {
                params['active'] = this._active.toSQLDate();
            } else {
                params['active'] = this._active;
            }
        }

        if (this._activeBetween) {
            params['active_between'] = this._activeBetween;
        }

        this.fetchParams$.next(params);
    }

    private _buildOptions(carers: Carer[]): AppSelectOption<Carer>[] {
        return CarerPresenter.attachCollection(carers).map(carer => {
            let description: string;

            const unavailability = carer.unavailability?.data;

            if (unavailability) {
                description = `Unavailable: ${unavailability.reason}`;
            }

            return ({
                label: carer.presenter.fullNameLastFirst,
                value: carer,
                description,
                disabled: !!unavailability,
                has_avatar: true,
                avatar_url: carer.person.data.avatar_url
            });
        });
    }
}
